import React, { useEffect, useState } from 'react';
import { ModalTitle } from '../../../global/Styles/titles';
import { useApiClient, validateSuccess2xx } from '../../../../clients/apiAuth';
import { CustomToast, ErrorToast, OkToast } from '../../Toast';

const unidadOptions = [
  { value: 'GLL', label: 'Galones' },
  { value: 'KGM', label: 'Kilogramos' },
];

const tipoServicioOptions = [
  { value: '0', label: 'Servicio adicional' },
  { value: '1', label: 'Remesa de transp. Registrada en el RNDC' },
];

const formatValue = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const formatPeso = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return formatValue(value);
  return parsed.toLocaleString();
};

const getRutaLabel = (solicitud) => {
  const origen = solicitud?.ORIGEN || '';
  const destino = solicitud?.DESTINO || '';
  return origen && destino ? `${origen} -> ${destino}` : '-';
};

const buildInitialForm = (vehiculo) => ({
  MANIFIESTO_CANTIDAD: formatValue(vehiculo?.MANIFIESTO_CANTIDAD, ''),
  MANIFIESTO_UNIDAD: formatValue(vehiculo?.MANIFIESTO_UNIDAD, unidadOptions[0].value),
  MANIFIESTO_TIPO_SERVICIO: formatValue(vehiculo?.MANIFIESTO_TIPO_SERVICIO, tipoServicioOptions[1].value),
  MANIFIESTO_CONSECUTIVO: formatValue(vehiculo?.MANIFIESTO_CONSECUTIVO, ''),
  MANIFIESTO_REMESA: formatValue(vehiculo?.MANIFIESTO_REMESA, ''),
  MANIFIESTO_RNDC: formatValue(vehiculo?.MANIFIESTO_RNDC, ''),
});

const ModalConfirmarManifiesto = ({ isOpen, onClose, vehiculo, solicitud, auth, onSuccess }) => {
  const { post } = useApiClient();
  const [form, setForm] = useState(buildInitialForm(vehiculo));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputBase = 'w-full rounded border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-blue-500 bg-white';
  const inputError = 'border-red-500';
  const inputOk = 'border-gray-300';

  useEffect(() => {
    if (!isOpen) return;
    setForm(buildInitialForm(vehiculo));
    setErrors({});
  }, [isOpen, vehiculo]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    const requiredFields = [
      'MANIFIESTO_CANTIDAD',
      'MANIFIESTO_UNIDAD',
      'MANIFIESTO_TIPO_SERVICIO',
      'MANIFIESTO_CONSECUTIVO',
      'MANIFIESTO_REMESA',
      'MANIFIESTO_RNDC',
    ];
    requiredFields.forEach((field) => {
      if (!form[field]) {
        nextErrors[field] = '*';
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      ErrorToast('Error: ', 'Complete los campos obligatorios.');
      return;
    }

    const vehiculoTipoId = vehiculo?.SOLICITUD_VEHICULO_TIPO_ID ?? vehiculo?.solicitud_vehiculo_tipo_id;
    if (!vehiculoTipoId) {
      ErrorToast('Error: ', 'No se encontró el tipo de vehículo.');
      return;
    }

    const payload = {
      SOLICITUD_VEHICULO_TIPO_ID: vehiculoTipoId,
      SOLICITUD_ID: solicitud?.SOLICITUD_ID,
      TERCERO_ID: auth?.data?.user?.TERCERO_ID,
      MANIFIESTO_CANTIDAD: form.MANIFIESTO_CANTIDAD,
      MANIFIESTO_UNIDAD: form.MANIFIESTO_UNIDAD,
      MANIFIESTO_TIPO_SERVICIO: form.MANIFIESTO_TIPO_SERVICIO,
      MANIFIESTO_CONSECUTIVO: form.MANIFIESTO_CONSECUTIVO,
      MANIFIESTO_REMESA: form.MANIFIESTO_REMESA,
      MANIFIESTO_RNDC: form.MANIFIESTO_RNDC,
      MANIFIESTO_ESTADO_ACC_ID: 3,
      MANIFIESTO_ESTADO_ACC_INI: 1,
      MANIFIESTO_ESTADO_ACC_FIN: 2,
      MANIFIESTO_ESTADO_ACC_DESC: 'Confirmar manifiesto',
      MANIFIESTO_ESTADO_ID: 2,
      SOLICITUD_MANIFIESTO_OBSERVACION: '.',
    };

    setIsSubmitting(true);
    try {
      const result = await post(
        `/solicitudVehiculoTipo/${vehiculoTipoId}/notificarManifiesto`,
        {
          json: JSON.stringify(payload),
        },
        'application/json'
      );

      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        return;
      }

      OkToast({ message: 'Manifiesto guardado!' });
      if (typeof onSuccess === 'function') {
        onSuccess(payload);
      }
      onClose();
    } catch (error) {
      ErrorToast('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const placa = formatValue(vehiculo?.VEHICULO_PLACA);
  const peso = formatPeso(vehiculo?.SOLICITUD_PESO);
  const cliente = formatValue(solicitud?.CLIENTE_NOMBRE);
  const ruta = getRutaLabel(solicitud);

  return (
    <div className="min-w-screen animated fadeIn faster outline-none focus:outline-none fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-4">
      <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
      <div className="relative mx-auto my-auto w-full max-w-3xl rounded-xl bg-white shadow-lg">
        <ModalTitle>CONFIRMAR MANIFIESTO</ModalTitle>
        <div className="p-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="grid grid-cols-1 gap-3 text-sm laptop:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Placa</p>
                <p className="font-medium text-gray-800">{placa}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Peso</p>
                <p className="font-medium text-gray-800">{peso}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Cliente</p>
                <p className="font-medium text-gray-800">{cliente}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Ruta</p>
                <p className="font-medium text-gray-800">{ruta}</p>
              </div>
            </div>
          </div>

          <form
            className="mt-6"
            onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 laptop:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-gray-600">Manifiesto consecutivo</p>
                <input
                  type="text"
                  value={form.MANIFIESTO_CONSECUTIVO}
                  onChange={handleChange('MANIFIESTO_CONSECUTIVO')}
                  className={`${inputBase} ${errors.MANIFIESTO_CONSECUTIVO ? inputError : inputOk}`}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Remesa</p>
                <input
                  type="text"
                  value={form.MANIFIESTO_REMESA}
                  onChange={handleChange('MANIFIESTO_REMESA')}
                  className={`${inputBase} ${errors.MANIFIESTO_REMESA ? inputError : inputOk}`}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">RNDC</p>
                <input
                  type="text"
                  value={form.MANIFIESTO_RNDC}
                  onChange={handleChange('MANIFIESTO_RNDC')}
                  className={`${inputBase} ${errors.MANIFIESTO_RNDC ? inputError : inputOk}`}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Cantidad transportada</p>
                <input
                  type="text"
                  value={form.MANIFIESTO_CANTIDAD}
                  onChange={handleChange('MANIFIESTO_CANTIDAD')}
                  className={`${inputBase} ${errors.MANIFIESTO_CANTIDAD ? inputError : inputOk}`}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Unidad de medida</p>
                <select
                  value={form.MANIFIESTO_UNIDAD}
                  onChange={handleChange('MANIFIESTO_UNIDAD')}
                  className={`${inputBase} ${errors.MANIFIESTO_UNIDAD ? inputError : inputOk}`}>
                  {unidadOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Tipo de servicio</p>
                <select
                  value={form.MANIFIESTO_TIPO_SERVICIO}
                  onChange={handleChange('MANIFIESTO_TIPO_SERVICIO')}
                  className={`${inputBase} ${errors.MANIFIESTO_TIPO_SERVICIO ? inputError : inputOk}`}>
                  {tipoServicioOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded bg-gray-600 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmarManifiesto;
